import { useEffect, useState } from "react";
import { Moon, Sun } from "lucide-react";

/**
 * Theme toggle — flips html[data-theme] between 'dark' (default) and
 * 'light'. Persists the user's choice in localStorage. Defaults to
 * dark unless the user has previously chosen light.
 */

type Theme = "dark" | "light";
const STORAGE_KEY = "indstartups.theme";

function readInitial(): Theme {
  if (typeof window === "undefined") return "dark";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "light" || stored === "dark") return stored;
  return "dark";
}

function applyTheme(t: Theme) {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", t);
  // Also update the meta theme-color so mobile chrome reflects the bg.
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", t === "light" ? "#faf6ec" : "#0f0a1a");
}

export function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(readInitial);

  useEffect(() => { applyTheme(theme); }, [theme]);

  const toggle = () => {
    const next: Theme = theme === "dark" ? "light" : "dark";
    window.localStorage.setItem(STORAGE_KEY, next);
    setTheme(next);
  };

  return (
    <button
      onClick={toggle}
      aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
      className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-amber-400/25 bg-plum-950/40
                 hover:bg-amber-400/12 hover:border-amber-400/55 text-cream-50
                 transition-colors"
    >
      {theme === "dark"
        ? <Sun  className="w-4 h-4 text-amber-300" aria-hidden />
        : <Moon className="w-4 h-4 text-amber-500" aria-hidden />
      }
    </button>
  );
}
